import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const btnStyle = {
  padding: "4px 14px",
  fontFamily: "Arial, sans-serif",
  fontSize: "14px",
  cursor: "pointer",
  backgroundColor: "#ececec",
  border: "2px outset #ccc",
  color: "#000",
  borderRadius: "2px",
};
const inputStyle = {
  display: "block",
  width: "100%",
  padding: "6px 8px",
  fontFamily: "Arial, sans-serif",
  fontSize: "14px",
  border: "1px solid #999",
  borderRadius: "2px",
  marginBottom: "8px",
  boxSizing: "border-box",
  backgroundColor: "#fff",
};
// Shared tab-underline style (Live/Demo and All/Visible/Hidden)
const tabBtn = (isActive) => ({
  fontFamily: "Arial, sans-serif",
  fontSize: "13px",
  background: "none",
  border: "none",
  cursor: "pointer",
  paddingBottom: "3px",
  paddingLeft: 0,
  paddingRight: 0,
  marginRight: "16px",
  borderBottom: isActive ? "2px solid #111" : "2px solid transparent",
  fontWeight: isActive ? "bold" : "normal",
  color: isActive ? "#111" : "#555",
});

export default function Admin() {
  const [password, setPassword]     = useState("");
  const [token, setToken]           = useState(() => localStorage.getItem("admin_token") || null);
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn]   = useState(false);

  // Live posts
  const [ideas, setIdeas]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState("all");

  // Site status (live)
  const [siteStatus, setSiteStatus] = useState("live");
  const [statusBusy, setStatusBusy] = useState(false);
  // Demo site status (independent)
  const [demoSiteStatus, setDemoSiteStatus] = useState("live");
  const [demoPauseBusy, setDemoPauseBusy] = useState(false);

  // Live participant counters
  const [participants, setParticipants] = useState({ today: 0, total: 0 });

  // Report notification email
  const [notifEmail, setNotifEmail]         = useState("");
  const [notifEmailInput, setNotifEmailInput] = useState("");
  const [notifEmailMsg, setNotifEmailMsg]   = useState("");
  const [notifEmailBusy, setNotifEmailBusy] = useState(false);

  // Change password
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew]         = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [cpError, setCpError]     = useState("");
  const [cpSuccess, setCpSuccess] = useState("");
  const [cpBusy, setCpBusy]       = useState(false);

  // Password visibility
  const [showLoginPw,   setShowLoginPw]   = useState(false);
  const [showCpCurrent, setShowCpCurrent] = useState(false);
  const [showCpNew,     setShowCpNew]     = useState(false);
  const [showCpConfirm, setShowCpConfirm] = useState(false);

  // ── Live / Demo tab ───────────────────────────────────────────────────────
  const [adminView, setAdminView] = useState("live"); // "live" | "demo"

  // ── Demo state ────────────────────────────────────────────────────────────
  const [demoIdeas, setDemoIdeas]             = useState([]);
  const [demoLoading, setDemoLoading]         = useState(false);
  const [demoFilter, setDemoFilter]           = useState("all");
  const [demoVisits, setDemoVisits]           = useState({ today: 0, total: 0 });
  const [demoStateInfo, setDemoStateInfo]     = useState(null);
  const [demoStateMsg, setDemoStateMsg]       = useState("");
  const [demoStateBusy, setDemoStateBusy]     = useState(false);
  // Create new demo post
  const [newDemoText, setNewDemoText]   = useState("");
  const [newDemoType, setNewDemoType]   = useState("would");
  const [newDemoCount, setNewDemoCount] = useState(0);
  const [newDemoError, setNewDemoError] = useState("");
  const [newDemoBusy, setNewDemoBusy]   = useState(false);
  // Inline count editor per post: { [id]: inputValue }
  const [demoCountInputs, setDemoCountInputs] = useState({});
  // Full edit mode per post
  const [editingDemoId, setEditingDemoId] = useState(null);
  const [editDemoText, setEditDemoText]   = useState("");
  const [editDemoType, setEditDemoType]   = useState("would");
  const [editDemoError, setEditDemoError] = useState("");
  const [editDemoBusy, setEditDemoBusy]   = useState(false);
  // Export / Import
  const [exportBusy, setExportBusy]         = useState(false);
  const [importFile, setImportFile]         = useState(null);
  const [importBusy, setImportBusy]         = useState(false);
  const [importResult, setImportResult]     = useState(null);
  const [importError, setImportError]       = useState("");

  const showHideStyle = {
    background: "none", border: "none", padding: 0,
    fontFamily: "Arial", fontSize: "13px", color: "#555",
    textDecoration: "underline", cursor: "pointer", flexShrink: 0,
  };

  const handleAuthError = (err) => {
    const detail = err.response?.data?.detail;
    if (err.response?.status === 401 || detail === "SESSION_INVALIDATED") {
      localStorage.removeItem("admin_token");
      setToken(null);
    }
  };

  // ── Auth ─────────────────────────────────────────────────────────────────

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      if (err.response?.status === 429) {
        setLoginError("Too many login attempts. Please try again later.");
      } else {
        setLoginError("Invalid password.");
      }
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIdeas([]);
  };

  // ── Live data loaders ─────────────────────────────────────────────────────

  const loadIdeas = async (tk) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ideas`, { headers: { Authorization: `Bearer ${tk}` } });
      setIdeas(res.data.ideas);
    } catch (err) { handleAuthError(err); }
    finally { setLoading(false); }
  };

  const loadSiteStatus = async () => {
    try {
      const res = await axios.get(`${API}/status`);
      setSiteStatus(res.data.paused ? "paused" : "live");
    } catch {}
  };

  const loadDemoSiteStatus = async () => {
    try {
      const res = await axios.get(`${API}/demo/status`);
      setDemoSiteStatus(res.data.paused ? "paused" : "live");
    } catch {}
  };

  const loadVisits = async (tk) => {
    try {
      const res = await axios.get(`${API}/admin/visits`, { headers: { Authorization: `Bearer ${tk}` } });
      setParticipants({ today: res.data.today ?? 0, total: res.data.total ?? 0 });
    } catch {}
  };

  const loadNotifEmail = async (tk) => {
    try {
      const res = await axios.get(`${API}/admin/notification-email`, { headers: { Authorization: `Bearer ${tk}` } });
      setNotifEmail(res.data.email ?? "");
      setNotifEmailInput(res.data.email ?? "");
    } catch {}
  };

  // ── Demo data loaders ─────────────────────────────────────────────────────

  const loadDemoData = async (tk) => {
    setDemoLoading(true);
    try {
      const [ideasRes, visitsRes, stateRes] = await Promise.all([
        axios.get(`${API}/admin/demo/ideas`, { headers: { Authorization: `Bearer ${tk}` } }),
        axios.get(`${API}/admin/demo/visits`, { headers: { Authorization: `Bearer ${tk}` } }),
        axios.get(`${API}/admin/demo/starting-state`, { headers: { Authorization: `Bearer ${tk}` } }),
      ]);
      setDemoIdeas(ideasRes.data.ideas);
      setDemoVisits({ today: visitsRes.data.today ?? 0, total: visitsRes.data.total ?? 0 });
      setDemoStateInfo(stateRes.data);
      setDemoCountInputs({}); // clear any stale inline drafts
    } catch (err) { handleAuthError(err); }
    finally { setDemoLoading(false); }
  };

  useEffect(() => {
    if (token) {
      loadIdeas(token);
      loadSiteStatus();
      loadDemoSiteStatus();
      loadVisits(token);
      loadNotifEmail(token);
      loadDemoData(token);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live actions ──────────────────────────────────────────────────────────

  const toggleVisibility = async (idea) => {
    try {
      await axios.patch(
        `${API}/admin/ideas/${idea.id}`,
        { hidden: !idea.hidden },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIdeas((prev) => prev.map((i) => i.id === idea.id ? { ...i, hidden: !i.hidden } : i));
    } catch (err) { handleAuthError(err); }
  };

  const clearReport = async (idea) => {
    try {
      await axios.post(
        `${API}/admin/ideas/${idea.id}/clear-report`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIdeas((prev) => prev.map((i) => i.id === idea.id ? { ...i, reported: false, reported_at: null } : i));
    } catch (err) { handleAuthError(err); }
  };

  const pauseSite = async () => {
    if (!window.confirm("Pause the site? New submissions and help activity will be disabled until you resume it.")) return;
    setStatusBusy(true);
    try {
      await axios.post(`${API}/admin/pause`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSiteStatus("paused");
    } catch (err) { handleAuthError(err); }
    finally { setStatusBusy(false); }
  };

  const resumeSite = async () => {
    setStatusBusy(true);
    try {
      await axios.post(`${API}/admin/resume`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSiteStatus("live");
    } catch (err) { handleAuthError(err); }
    finally { setStatusBusy(false); }
  };

  const pauseDemo = async () => {
    if (!window.confirm("Pause the demo? Demo visitors won't be able to submit ideas or toggle willingness until you resume it.")) return;
    setDemoPauseBusy(true);
    try {
      await axios.post(`${API}/admin/demo/pause`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDemoSiteStatus("paused");
    } catch (err) { handleAuthError(err); }
    finally { setDemoPauseBusy(false); }
  };

  const resumeDemo = async () => {
    setDemoPauseBusy(true);
    try {
      await axios.post(`${API}/admin/demo/resume`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDemoSiteStatus("live");
    } catch (err) { handleAuthError(err); }
    finally { setDemoPauseBusy(false); }
  };

  const saveNotifEmail = async (e) => {
    e.preventDefault();
    setNotifEmailBusy(true);
    setNotifEmailMsg("");
    try {
      const res = await axios.put(
        `${API}/admin/notification-email`,
        { email: notifEmailInput.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifEmail(res.data.email);
      setNotifEmailInput(res.data.email);
      setNotifEmailMsg("Saved.");
    } catch (err) {
      setNotifEmailMsg(err.response?.data?.detail || "Could not save.");
      handleAuthError(err);
    } finally { setNotifEmailBusy(false); }
  };

  const resetParticipants = async () => {
    if (!window.confirm("Reset the live participant count to 0?")) return;
    try {
      await axios.post(`${API}/admin/reset-participants`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setParticipants({ today: 0, total: 0 });
    } catch (err) { handleAuthError(err); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setCpError(""); setCpSuccess(""); setCpBusy(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: cpCurrent, new_password: cpNew, confirm_password: cpConfirm },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCpSuccess("Password changed. Please log in again.");
      setCpCurrent(""); setCpNew(""); setCpConfirm("");
      setTimeout(() => { localStorage.removeItem("admin_token"); setToken(null); }, 1500);
    } catch (err) {
      setCpError(err.response?.data?.detail || "Something went wrong.");
      handleAuthError(err);
    } finally { setCpBusy(false); }
  };

  // ── Demo actions ──────────────────────────────────────────────────────────

  const createDemoPost = async (e) => {
    e.preventDefault();
    if (!newDemoText.trim()) return;
    setNewDemoBusy(true); setNewDemoError("");
    try {
      const res = await axios.post(
        `${API}/admin/demo/ideas`,
        { text: newDemoText.trim(), post_type: newDemoType, admin_count: parseInt(newDemoCount, 10) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDemoIdeas((prev) => [res.data, ...prev]);
      setNewDemoText(""); setNewDemoCount(0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setNewDemoError(
        detail === "AI_MODERATION_BLOCKED" || detail === "CONTACT_INFO_BLOCKED"
          ? "Content blocked by moderation. Please revise."
          : detail || "Could not create post."
      );
      handleAuthError(err);
    } finally { setNewDemoBusy(false); }
  };

  const saveInlineCount = async (id, rawValue) => {
    const newCount = Math.max(0, parseInt(rawValue, 10) || 0);
    try {
      const res = await axios.put(
        `${API}/admin/demo/ideas/${id}`,
        { admin_count: newCount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDemoIdeas((prev) => prev.map((i) => i.id === id ? res.data : i));
      setDemoCountInputs((prev) => { const next = { ...prev }; delete next[id]; return next; });
    } catch (err) { handleAuthError(err); }
  };

  const startEditDemo = (idea) => {
    setEditingDemoId(idea.id);
    setEditDemoText(idea.text);
    setEditDemoType(idea.post_type || "would");
    setEditDemoError("");
  };

  const saveEditDemo = async (id) => {
    setEditDemoBusy(true); setEditDemoError("");
    try {
      const res = await axios.put(
        `${API}/admin/demo/ideas/${id}`,
        { text: editDemoText.trim(), post_type: editDemoType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDemoIdeas((prev) => prev.map((i) => i.id === id ? res.data : i));
      setEditingDemoId(null);
    } catch (err) {
      setEditDemoError(err.response?.data?.detail || "Could not save.");
      handleAuthError(err);
    } finally { setEditDemoBusy(false); }
  };

  const toggleDemoVisibility = async (idea) => {
    try {
      const res = await axios.put(
        `${API}/admin/demo/ideas/${idea.id}`,
        { hidden: !idea.hidden },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDemoIdeas((prev) => prev.map((i) => i.id === idea.id ? res.data : i));
    } catch (err) { handleAuthError(err); }
  };

  const deleteDemoIdea = async (id) => {
    if (!window.confirm("Permanently delete this demo post?")) return;
    try {
      await axios.delete(`${API}/admin/demo/ideas/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDemoIdeas((prev) => prev.filter((i) => i.id !== id));
    } catch (err) { handleAuthError(err); }
  };

  const clearDemoReport = async (idea) => {
    try {
      await axios.post(
        `${API}/admin/demo/ideas/${idea.id}/clear-report`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDemoIdeas((prev) => prev.map((i) => i.id === idea.id ? { ...i, reported: false, reported_at: null } : i));
    } catch (err) { handleAuthError(err); }
  };

  const saveDemoState = async () => {
    if (!window.confirm("Save the current demo as the starting state?\nThis will overwrite any previously saved starting state.")) return;
    setDemoStateBusy(true); setDemoStateMsg("");
    try {
      const res = await axios.post(`${API}/admin/demo/save-state`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDemoStateInfo({ exists: true, saved_at: res.data.saved_at, post_count: res.data.post_count });
      setDemoStateMsg(`Saved — ${res.data.post_count} post${res.data.post_count !== 1 ? "s" : ""}.`);
    } catch (err) {
      setDemoStateMsg("Could not save.");
      handleAuthError(err);
    } finally { setDemoStateBusy(false); }
  };

  const resetDemo = async () => {
    if (!demoStateInfo?.exists) { alert("No starting state saved yet. Save one first."); return; }
    if (!window.confirm(
      `Reset the demo to the saved starting state (${demoStateInfo.post_count} post${demoStateInfo.post_count !== 1 ? "s" : ""})?\n\nThis removes posts and visitor signals added after the last save.`
    )) return;
    setDemoStateBusy(true); setDemoStateMsg("");
    try {
      await axios.post(`${API}/admin/demo/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await loadDemoData(token);
      setDemoStateMsg("Demo reset to saved starting state.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDemoStateMsg(detail === "NO_STARTING_STATE" ? "No starting state saved." : "Reset failed.");
      handleAuthError(err);
    } finally { setDemoStateBusy(false); }
  };

  const downloadExport = async () => {
    setExportBusy(true);
    try {
      const res = await axios.get(`${API}/admin/demo/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `demo_backup_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed. Please try again.");
      handleAuthError(err);
    } finally { setExportBusy(false); }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImportBusy(true); setImportResult(null); setImportError("");
    try {
      const text = await importFile.text();
      let data;
      try { data = JSON.parse(text); }
      catch { throw new Error("Invalid JSON file. Please select a valid demo backup."); }
      if (data.export_version !== "1.0") {
        throw new Error(`Unsupported backup version: ${data.export_version}. Expected 1.0.`);
      }
      const res = await axios.post(`${API}/admin/demo/import`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImportResult(res.data);
      await loadDemoData(token);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Import failed.";
      setImportError(detail);
      handleAuthError(err);
    } finally { setImportBusy(false); }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const displayed = ideas.filter((i) => {
    if (filter === "visible") return !i.hidden;
    if (filter === "hidden") return i.hidden;
    return true;
  });

  const demoDisplayed = demoIdeas.filter((i) => {
    if (demoFilter === "visible") return !i.hidden;
    if (demoFilter === "hidden") return i.hidden;
    return true;
  });

  // ── Login form ────────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "#FFF4B8", fontFamily: "Arial, sans-serif" }}>
        <div className="max-w-md mx-auto px-4 py-16">
          <h1 className="text-2xl font-bold text-[#111111] border-b-2 border-[#111111] pb-4 mb-8">Admin</h1>
          <form data-testid="admin-login-form" onSubmit={login} className="space-y-4">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                data-testid="admin-password-input"
                type={showLoginPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                className="ph-no-capture w-full p-4 border-2 border-[#111111] bg-white text-[#111111] focus:outline-none focus:ring-2 focus:ring-[#111111] rounded-none text-base"
              />
              <button type="button" onClick={() => setShowLoginPw((p) => !p)} style={showHideStyle}>
                {showLoginPw ? "Hide" : "Show"}
              </button>
            </div>
            {loginError && <p className="text-red-600 text-sm font-mono">{loginError}</p>}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loggingIn || !password}
              className="w-full py-3 bg-[#111111] text-white font-medium text-base border-2 border-[#111111] rounded-none disabled:opacity-40 hover:bg-neutral-800"
            >
              {loggingIn ? "Logging in\u2026" : "Login"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Admin panel ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFF4B8", fontFamily: "Arial, sans-serif" }}>
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="flex items-center justify-between border-b-2 border-[#111111] pb-4 mb-6">
          <h1 style={{ fontFamily: "Arial", fontWeight: "bold", fontSize: "20px", margin: 0 }}>Admin Panel</h1>
          <button onClick={logout} style={{ fontFamily: "Arial", fontSize: "13px", color: "#555", textDecoration: "underline", background: "none", border: "none", cursor: "pointer" }}>
            Logout
          </button>
        </div>

        {/* ── Site status — main and demo, side by side ──────── */}
        <div style={{ marginBottom: "24px", display: "flex", gap: "12px", flexWrap: "wrap" }}>

          {/* Main site */}
          <div data-testid="site-status-section" style={{ flex: 1, minWidth: "200px", padding: "12px", border: "1px solid #bbb", backgroundColor: "#fff" }}>
            <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#888", margin: "0 0 4px" }}>Main site (/)</p>
            <p style={{ fontFamily: "Arial", fontSize: "14px", margin: "0 0 8px" }}>
              Status:{" "}
              <strong style={{ color: siteStatus === "paused" ? "#cc0000" : "#006600" }}>
                {siteStatus === "paused" ? "PAUSED" : "LIVE"}
              </strong>
            </p>
            {siteStatus === "paused" ? (
              <button data-testid="resume-site-button" onClick={resumeSite} disabled={statusBusy}
                style={{ ...btnStyle, ...(statusBusy ? { opacity: 0.5, cursor: "default" } : {}) }}>
                {statusBusy ? "Resuming\u2026" : "Resume main site"}
              </button>
            ) : (
              <button data-testid="pause-site-button" onClick={pauseSite} disabled={statusBusy}
                style={{ ...btnStyle, ...(statusBusy ? { opacity: 0.5, cursor: "default" } : {}) }}>
                {statusBusy ? "Pausing\u2026" : "Pause main site"}
              </button>
            )}
          </div>

          {/* Demo site */}
          <div data-testid="demo-status-section" style={{ flex: 1, minWidth: "200px", padding: "12px", border: "1px solid #bbb", backgroundColor: "#fff" }}>
            <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#888", margin: "0 0 4px" }}>Demo (/demo)</p>
            <p style={{ fontFamily: "Arial", fontSize: "14px", margin: "0 0 8px" }}>
              Status:{" "}
              <strong style={{ color: demoSiteStatus === "paused" ? "#cc0000" : "#006600" }}>
                {demoSiteStatus === "paused" ? "PAUSED" : "LIVE"}
              </strong>
            </p>
            {demoSiteStatus === "paused" ? (
              <button data-testid="resume-demo-button" onClick={resumeDemo} disabled={demoPauseBusy}
                style={{ ...btnStyle, ...(demoPauseBusy ? { opacity: 0.5, cursor: "default" } : {}) }}>
                {demoPauseBusy ? "Resuming\u2026" : "Resume demo"}
              </button>
            ) : (
              <button data-testid="pause-demo-button" onClick={pauseDemo} disabled={demoPauseBusy}
                style={{ ...btnStyle, ...(demoPauseBusy ? { opacity: 0.5, cursor: "default" } : {}) }}>
                {demoPauseBusy ? "Pausing\u2026" : "Pause demo"}
              </button>
            )}
          </div>

        </div>

        {/* ── Live participants ─────────────────────────────── */}
        <div data-testid="participants-section" style={{ marginBottom: "24px", padding: "12px", border: "1px solid #bbb", backgroundColor: "#fff" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "6px" }}>
            <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#888", margin: 0 }}>Live site &mdash; participants</p>
            <button data-testid="reset-participants-button" onClick={resetParticipants}
              style={{ ...btnStyle, fontSize: "12px", padding: "2px 10px" }}>
              Reset
            </button>
          </div>
          <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#333", margin: "0 0 3px" }}>
            Today: <span data-testid="participants-today">{participants.today}</span>
          </p>
          <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#333", margin: 0 }}>
            Total: <span data-testid="participants-total">{participants.total}</span>
          </p>
        </div>

        {/* ══════════════════════════════════════════════════
            LIVE | DEMO TAB SWITCH
            ══════════════════════════════════════════════════ */}
        <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #ccc", marginBottom: "16px" }}>
          <button
            data-testid="admin-tab-live"
            onClick={() => setAdminView("live")}
            style={tabBtn(adminView === "live")}
          >
            Live
          </button>
          <button
            data-testid="admin-tab-demo"
            onClick={() => setAdminView("demo")}
            style={tabBtn(adminView === "demo")}
          >
            Demo
          </button>
        </div>

        {/* ══ LIVE VIEW ══════════════════════════════════════ */}
        {adminView === "live" && (
          <>
            {/* All / Visible / Hidden filter */}
            <div className="flex gap-4 mb-4">
              {["all", "visible", "hidden"].map((f) => (
                <button key={f} onClick={() => setFilter(f)} style={tabBtn(filter === f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <span style={{ marginLeft: "auto", fontFamily: "Arial", fontSize: "13px", color: "#555" }}>
                {displayed.length} shown
              </span>
            </div>

            {loading ? (
              <p style={{ fontFamily: "Arial", color: "#666", fontSize: "14px" }}>Loading\u2026</p>
            ) : (
              <div data-testid="admin-posts-table" style={{ marginBottom: "32px" }}>
                {displayed.length === 0 && (
                  <p style={{ fontFamily: "Arial", color: "#666", fontSize: "14px" }}>No ideas.</p>
                )}
                {displayed.map((idea) => (
                  <div
                    key={idea.id}
                    style={{
                      padding: "10px 12px", border: "1px solid #bbb", backgroundColor: "#fff",
                      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                      gap: "12px", marginBottom: "6px", opacity: idea.hidden ? 0.5 : 1,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontFamily: "Arial", fontSize: "14px", color: "#111", margin: "0 0 4px", wordBreak: "break-word", lineHeight: "1.4" }}>
                        {idea.text}
                      </p>
                      <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#666", margin: 0 }}>
                        {idea.help_count} {idea.post_type === "wish" ? "would try this" : "would help"} &middot;{" "}
                        {new Date(idea.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}{" "}
                        &middot; <span style={{ fontWeight: idea.hidden ? "bold" : "normal" }}>{idea.hidden ? "HIDDEN" : "VISIBLE"}</span>
                        {idea.post_type === "wish" && (
                          <> &middot; <span data-testid="admin-wish-label" style={{ color: "#555", fontStyle: "italic" }}>What would people try to make happen?</span></>
                        )}
                        {idea.reported && (
                          <> &middot; <span data-testid="admin-reported-label" style={{ color: "#cc0000", fontWeight: "bold" }}>REPORTED</span></>
                        )}
                      </p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", flexShrink: 0 }}>
                      <button data-testid="admin-toggle-visibility-button" onClick={() => toggleVisibility(idea)}
                        style={{ ...btnStyle, fontSize: "12px" }}>
                        {idea.hidden ? "Restore" : "Hide"}
                      </button>
                      {idea.reported && (
                        <button data-testid="admin-clear-report-button" onClick={() => clearReport(idea)}
                          style={{ ...btnStyle, fontSize: "11px" }}>
                          Clear report
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ══ DEMO VIEW ══════════════════════════════════════ */}
        {adminView === "demo" && (
          <div data-testid="demo-admin-section">

            {/* Demo participants */}
            <div style={{ marginBottom: "16px", padding: "10px 12px", border: "1px solid #bbb", backgroundColor: "#fff" }}>
              <p style={{ fontFamily: "Arial", fontSize: "13px", fontWeight: "bold", margin: "0 0 4px" }}>
                Demo participants &nbsp;
                <a href="/demo" target="_blank" rel="noreferrer" style={{ fontWeight: "normal", fontSize: "12px", color: "blue", textDecoration: "underline" }}>
                  view demo &rarr;
                </a>
              </p>
              <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#333", margin: 0 }}>
                Today: {demoVisits.today} &nbsp;&middot;&nbsp; Total: {demoVisits.total}
              </p>
            </div>

            {/* Starting state — save & reset */}
            <div style={{ marginBottom: "16px", padding: "10px 12px", border: "1px solid #bbb", backgroundColor: "#fff" }}>
              <p style={{ fontFamily: "Arial", fontSize: "13px", fontWeight: "bold", margin: "0 0 6px" }}>Starting state</p>
              {demoStateInfo?.exists ? (
                <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#555", margin: "0 0 8px" }}>
                  Last saved: {new Date(demoStateInfo.saved_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}{" "}
                  &mdash; {demoStateInfo.post_count} post{demoStateInfo.post_count !== 1 ? "s" : ""}
                </p>
              ) : (
                <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#888", margin: "0 0 8px", fontStyle: "italic" }}>
                  No starting state saved yet.
                </p>
              )}
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button data-testid="demo-save-state-button" onClick={saveDemoState} disabled={demoStateBusy}
                  style={{ ...btnStyle, ...(demoStateBusy ? { opacity: 0.5, cursor: "default" } : {}) }}>
                  {demoStateBusy ? "Working\u2026" : "Save starting state"}
                </button>
                <button data-testid="demo-reset-button" onClick={resetDemo}
                  disabled={demoStateBusy || !demoStateInfo?.exists}
                  style={{ ...btnStyle, ...(demoStateBusy || !demoStateInfo?.exists ? { opacity: 0.5, cursor: "default" } : {}) }}>
                  Reset demo
                </button>
              </div>
              {demoStateMsg && (
                <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#333", margin: "6px 0 0" }}>{demoStateMsg}</p>
              )}
            </div>

          {/* ── Backup & Import ── */}
          <div style={{ marginBottom: "16px", padding: "10px 12px", border: "1px solid #bbb", backgroundColor: "#fff" }}>
            <p style={{ fontFamily: "Arial", fontSize: "13px", fontWeight: "bold", margin: "0 0 6px" }}>Backup &amp; import</p>

            {/* Export */}
            <div style={{ marginBottom: "12px" }}>
              <button
                data-testid="demo-export-button"
                onClick={downloadExport}
                disabled={exportBusy}
                style={{ ...btnStyle, ...(exportBusy ? { opacity: 0.5, cursor: "default" } : {}) }}
              >
                {exportBusy ? "Exporting\u2026" : "Download backup (JSON)"}
              </button>
              <p style={{ fontFamily: "Arial", fontSize: "11px", color: "#888", margin: "4px 0 0" }}>
                Exports all demo posts, counts, and starting state. Always current.
              </p>
            </div>

            {/* Import */}
            <div>
              <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#555", margin: "0 0 6px" }}>
                Import a backup to restore demo posts after publishing with "Keep existing production database".
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "6px" }}>
                <input
                  data-testid="demo-import-file-input"
                  type="file"
                  accept=".json"
                  onChange={(e) => {
                    setImportFile(e.target.files[0] || null);
                    setImportResult(null);
                    setImportError("");
                  }}
                  style={{ fontFamily: "Arial", fontSize: "12px" }}
                />
                <button
                  data-testid="demo-import-button"
                  onClick={handleImport}
                  disabled={importBusy || !importFile}
                  style={{ ...btnStyle, ...(importBusy || !importFile ? { opacity: 0.5, cursor: "default" } : {}) }}
                >
                  {importBusy ? "Importing\u2026" : "Import backup"}
                </button>
              </div>
              {importError && (
                <p data-testid="demo-import-error" style={{ fontFamily: "Arial", fontSize: "12px", color: "#cc0000", margin: "4px 0 0" }}>
                  {importError}
                </p>
              )}
              {importResult && (
                <p data-testid="demo-import-result" style={{ fontFamily: "Arial", fontSize: "12px", color: "#006600", margin: "4px 0 0" }}>
                  Import complete &mdash; {importResult.ideas_inserted + importResult.ideas_replaced_in_place} posts loaded
                  ({importResult.ideas_inserted} new, {importResult.ideas_replaced_in_place} updated in place).
                  Config: {importResult.config_imported.join(", ")}.
                  {importResult.ideas_errors > 0 && ` Errors: ${importResult.ideas_errors}.`}
                </p>
              )}
            </div>
          </div>


            {/* Add new demo post */}
            <div style={{ marginBottom: "16px", padding: "10px 12px", border: "1px solid #bbb", backgroundColor: "#fff" }}>
              <p style={{ fontFamily: "Arial", fontSize: "13px", fontWeight: "bold", margin: "0 0 8px" }}>Add demo post</p>
              <form onSubmit={createDemoPost}>
                <div style={{ display: "flex", gap: "4px", marginBottom: "6px", flexWrap: "wrap" }}>
                  {[["would", "WWYD"], ["wish", "DIP"]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setNewDemoType(val)}
                      style={{ ...btnStyle, fontSize: "12px", padding: "2px 10px", ...(newDemoType === val ? { border: "2px inset #ccc", backgroundColor: "#d0d0d0" } : {}) }}>
                      {label}
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="demo-new-post-text"
                  value={newDemoText}
                  onChange={(e) => { setNewDemoText(e.target.value); setNewDemoError(""); }}
                  rows={3}
                  placeholder={newDemoType === "wish" ? "What would you like to see people try to make happen?" : "I would..."}
                  style={{ ...inputStyle, minHeight: "70px", resize: "vertical", marginBottom: "6px" }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", flexWrap: "wrap" }}>
                  <label style={{ fontFamily: "Arial", fontSize: "12px", color: "#555", display: "flex", alignItems: "center", gap: "6px" }}>
                    Initial count:
                    <input
                      data-testid="demo-new-post-count"
                      type="number" min="0" value={newDemoCount}
                      onChange={(e) => setNewDemoCount(e.target.value)}
                      style={{ ...inputStyle, width: "70px", display: "inline-block", marginBottom: 0 }}
                    />
                  </label>
                </div>
                {newDemoError && (
                  <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#cc0000", margin: "0 0 6px" }}>{newDemoError}</p>
                )}
                <button data-testid="demo-add-post-button" type="submit"
                  disabled={newDemoBusy || !newDemoText.trim()}
                  style={{ ...btnStyle, ...(newDemoBusy || !newDemoText.trim() ? { opacity: 0.5, cursor: "default" } : {}) }}>
                  {newDemoBusy ? "Adding\u2026" : "Add demo post"}
                </button>
              </form>
            </div>

            {/* Demo posts list — All / Visible / Hidden filter */}
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
              {["all", "visible", "hidden"].map((f) => (
                <button key={f} onClick={() => setDemoFilter(f)} style={tabBtn(demoFilter === f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <span style={{ marginLeft: "auto", fontFamily: "Arial", fontSize: "13px", color: "#555" }}>
                {demoDisplayed.length} shown
              </span>
            </div>

            {demoLoading ? (
              <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#666" }}>Loading demo posts\u2026</p>
            ) : (
              <div data-testid="demo-admin-posts">
                {demoDisplayed.length === 0 && (
                  <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#888", fontStyle: "italic" }}>No demo posts.</p>
                )}
                {demoDisplayed.map((idea) => {
                  const isWish = idea.post_type === "wish";
                  const countLabel = isWish ? "Would try this" : "Would help";
                  const draftCount = demoCountInputs[idea.id] !== undefined ? demoCountInputs[idea.id] : idea.admin_count;
                  const isDirty = demoCountInputs[idea.id] !== undefined;

                  return (
                    <div
                      key={idea.id}
                      data-testid="demo-admin-post-row"
                      style={{
                        padding: "10px 12px", border: "1px solid #bbb", backgroundColor: "#fff",
                        marginBottom: "6px", opacity: idea.hidden ? 0.55 : 1,
                      }}
                    >
                      {editingDemoId === idea.id ? (
                        /* ── Text / type edit mode ── */
                        <div>
                          <div style={{ display: "flex", gap: "4px", marginBottom: "6px", flexWrap: "wrap" }}>
                            {[["would", "WWYD"], ["wish", "DIP"]].map(([val, label]) => (
                              <button key={val} type="button" onClick={() => setEditDemoType(val)}
                                style={{ ...btnStyle, fontSize: "11px", padding: "2px 8px", ...(editDemoType === val ? { border: "2px inset #ccc", backgroundColor: "#d0d0d0" } : {}) }}>
                                {label}
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={editDemoText}
                            onChange={(e) => setEditDemoText(e.target.value)}
                            rows={3}
                            style={{ ...inputStyle, width: "100%", resize: "vertical", marginBottom: "6px", boxSizing: "border-box" }}
                          />
                          {editDemoError && (
                            <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#cc0000", margin: "0 0 6px" }}>{editDemoError}</p>
                          )}
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button onClick={() => saveEditDemo(idea.id)} disabled={editDemoBusy || !editDemoText.trim()}
                              style={{ ...btnStyle, fontSize: "11px", ...(editDemoBusy ? { opacity: 0.5 } : {}) }}>
                              {editDemoBusy ? "Saving\u2026" : "Save"}
                            </button>
                            <button onClick={() => setEditingDemoId(null)} style={{ ...btnStyle, fontSize: "11px" }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ── View mode ── */
                        <div>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ fontFamily: "Arial", fontSize: "11px", color: "#777", margin: "0 0 2px" }}>
                                {isWish ? "DIP \u2014 What would you like to see people try?" : "WWYD \u2014 I would\u2026"}
                              </p>
                              <p style={{ fontFamily: "Arial", fontSize: "14px", color: "#111", margin: "0 0 4px", wordBreak: "break-word", lineHeight: "1.4" }}>
                                {idea.text}
                              </p>
                              <p style={{ fontFamily: "Arial", fontSize: "11px", color: "#666", margin: 0 }}>
                                Admin: {idea.admin_count} &nbsp;|&nbsp; Visitors: {idea.visitor_count} &nbsp;|&nbsp; Total: {idea.help_count} &nbsp;&middot;&nbsp;
                                {new Date(idea.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" })} &nbsp;&middot;&nbsp;
                                <span style={{ fontWeight: idea.hidden ? "bold" : "normal" }}>{idea.hidden ? "HIDDEN" : "VISIBLE"}</span>
                                {idea.reported && (
                                  <> &nbsp;&middot;&nbsp; <span data-testid="demo-reported-label" style={{ color: "#cc0000", fontWeight: "bold" }}>REPORTED</span></>
                                )}
                              </p>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", flexShrink: 0 }}>
                              <button data-testid="demo-edit-button" onClick={() => startEditDemo(idea)}
                                style={{ ...btnStyle, fontSize: "11px" }}>Edit text</button>
                              <button data-testid="demo-toggle-visibility-button" onClick={() => toggleDemoVisibility(idea)}
                                style={{ ...btnStyle, fontSize: "11px" }}>
                                {idea.hidden ? "Restore" : "Hide"}
                              </button>
                              <button data-testid="demo-delete-post-button" onClick={() => deleteDemoIdea(idea.id)}
                                style={{ ...btnStyle, fontSize: "11px" }}>Delete</button>
                              {idea.reported && (
                                <button data-testid="demo-clear-report-button" onClick={() => clearDemoReport(idea)}
                                  style={{ ...btnStyle, fontSize: "11px" }}>Clear report</button>
                              )}
                            </div>
                          </div>

                          {/* ── Inline count editor ── */}
                          <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #eee", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <label style={{ fontFamily: "Arial", fontSize: "12px", color: "#555", display: "flex", alignItems: "center", gap: "6px" }}>
                              {countLabel}:
                              <input
                                data-testid={`demo-count-input-${idea.id}`}
                                type="number"
                                min="0"
                                value={draftCount}
                                onChange={(e) => setDemoCountInputs((prev) => ({ ...prev, [idea.id]: e.target.value }))}
                                style={{ width: "64px", padding: "3px 6px", fontFamily: "Arial", fontSize: "13px", border: "1px solid #999", borderRadius: "2px", backgroundColor: "#fff" }}
                              />
                            </label>
                            <button
                              data-testid={`demo-count-save-${idea.id}`}
                              onClick={() => saveInlineCount(idea.id, draftCount)}
                              disabled={!isDirty}
                              style={{ ...btnStyle, fontSize: "11px", padding: "2px 10px", ...(isDirty ? {} : { opacity: 0.45, cursor: "default" }) }}
                            >
                              Save
                            </button>
                            {isDirty && (
                              <span style={{ fontFamily: "Arial", fontSize: "11px", color: "#888" }}>
                                Total will show: {Math.max(0, parseInt(draftCount, 10) || 0) + (idea.visitor_count || 0)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Report notifications ─────────────────────────── */}
        <div data-testid="notification-email-section" style={{ borderTop: "1px solid #bbb", paddingTop: "24px", marginTop: "32px", marginBottom: "32px" }}>
          <p style={{ fontFamily: "Arial", fontSize: "14px", fontWeight: "bold", margin: "0 0 4px" }}>Report notifications</p>
          <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#888", margin: "0 0 12px" }}>Live site only — demo reports do not trigger email.</p>
          <form onSubmit={saveNotifEmail} style={{ maxWidth: "360px" }}>
            <label style={{ display: "block", fontFamily: "Arial", fontSize: "13px", color: "#555", marginBottom: "4px" }}>Email</label>
            <input
              data-testid="notif-email-input"
              type="email"
              value={notifEmailInput}
              onChange={(e) => { setNotifEmailInput(e.target.value); setNotifEmailMsg(""); }}
              placeholder="admin@example.com"
              autoComplete="off"
              style={{ ...inputStyle, marginBottom: "8px" }}
            />
            {notifEmailMsg && (
              <p style={{ fontFamily: "Arial", fontSize: "13px", color: notifEmailMsg === "Saved." ? "#006600" : "#cc0000", margin: "0 0 8px" }}>
                {notifEmailMsg}
              </p>
            )}
            <button data-testid="notif-email-save" type="submit" disabled={notifEmailBusy}
              style={{ ...btnStyle, ...(notifEmailBusy ? { opacity: 0.5, cursor: "default" } : {}) }}>
              {notifEmailBusy ? "Saving\u2026" : "Save"}
            </button>
          </form>
        </div>

        {/* ── Change password ─────────────────────────────── */}
        <div data-testid="change-password-section" style={{ borderTop: "1px solid #bbb", paddingTop: "24px" }}>
          <p style={{ fontFamily: "Arial", fontSize: "14px", fontWeight: "bold", margin: "0 0 12px" }}>Change password</p>
          <form onSubmit={changePassword} style={{ maxWidth: "360px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <input data-testid="cp-current" type={showCpCurrent ? "text" : "password"} value={cpCurrent}
                onChange={(e) => setCpCurrent(e.target.value)} placeholder="Current password"
                autoComplete="current-password" className="ph-no-capture"
                style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
              <button type="button" onClick={() => setShowCpCurrent((p) => !p)} style={showHideStyle}>
                {showCpCurrent ? "Hide" : "Show"}
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <input data-testid="cp-new" type={showCpNew ? "text" : "password"} value={cpNew}
                onChange={(e) => setCpNew(e.target.value)} placeholder="New password (min. 12 characters)"
                autoComplete="new-password" className="ph-no-capture"
                style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
              <button type="button" onClick={() => setShowCpNew((p) => !p)} style={showHideStyle}>
                {showCpNew ? "Hide" : "Show"}
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <input data-testid="cp-confirm" type={showCpConfirm ? "text" : "password"} value={cpConfirm}
                onChange={(e) => setCpConfirm(e.target.value)} placeholder="Confirm new password"
                autoComplete="new-password" className="ph-no-capture"
                style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
              <button type="button" onClick={() => setShowCpConfirm((p) => !p)} style={showHideStyle}>
                {showCpConfirm ? "Hide" : "Show"}
              </button>
            </div>
            {cpError   && <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#cc0000", margin: "0 0 8px" }}>{cpError}</p>}
            {cpSuccess && <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#006600", margin: "0 0 8px" }}>{cpSuccess}</p>}
            <button data-testid="cp-submit" type="submit"
              disabled={cpBusy || !cpCurrent || !cpNew || !cpConfirm}
              style={{ ...btnStyle, ...(cpBusy || !cpCurrent || !cpNew || !cpConfirm ? { opacity: 0.5, cursor: "default" } : {}) }}>
              {cpBusy ? "Saving\u2026" : "Change password"}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
